/**
 * The profile wire protocol (PLAN-GALLERY G1).
 *
 * Shared by the Worker (which writes these responses and enforces every rule
 * below) and the browser (which reads them and applies the same rules early, so
 * a bad name is a red line under a field rather than a round trip). One module,
 * exactly as `share/protocol.ts` and `fermentation/protocol.ts` are, so the two
 * halves cannot drift.
 *
 * ```
 * public   GET    /api/avatars/:userId   the avatar image, or 404
 * session  GET    /api/profile           mine, or `profile: null`
 * session  PUT    /api/profile           claim or rename
 * session  PUT    /api/profile/avatar    raw image bytes
 * session  DELETE /api/profile/avatar    clear it
 * ```
 *
 * ## No profile is the normal state
 *
 * Most accounts never claim a name. Everything here therefore models a profile
 * as `Profile | null` rather than as a record with empty fields: G2's bylines
 * and G3's gallery both have to render "anonymous" without treating it as a
 * failure, and a null is the shape that makes that impossible to get wrong.
 *
 * ## Why the avatar is served from its own public prefix
 *
 * `/api/avatars/` sits in the Worker's `PUBLIC_API_PREFIXES` and the owner's
 * `/api/profile/*` routes do not, which is the same separation `/api/shared` and
 * `/api/share` make. The resource is public **by design** — it appears on public
 * share pages and in a public gallery — and the user id in the path is not an
 * authorization, it is an address: it names which image to send and grants
 * nothing. See `worker/profile.ts`.
 */

/** Where the owner's profile routes live. Session-guarded. */
export const PROFILE_BASE_PATH = '/api/profile';

/** Where the public avatar read lives. See the header for why it is its own prefix. */
export const AVATAR_PUBLIC_BASE_PATH = '/api/avatars';

/**
 * The `id` on the settings section, so {@link PROFILE_PAGE_PATH} has something
 * to hit. `account`, because the profile rows live inside Settings → Account —
 * the display name and picture merged into the section that describes the same
 * account privately, rather than holding a "Public profile" section of their
 * own.
 */
export const PROFILE_SECTION_ID = 'account';

/**
 * The settings section where a name is claimed, for every "claim a display
 * name" link — a fragment rather than a route, because the profile rows are part
 * of a Settings section and the settings page scrolls a `#fragment` to it.
 *
 * `SUPPORT_PAGE_PATH` was the other constant of this shape and stopped being one
 * in PLAN-SUPPORT: support grew a public half and needed a page. The profile rows
 * have not, and until they do a fragment is the honest address.
 */
export const PROFILE_PAGE_PATH = `/settings#${PROFILE_SECTION_ID}`;

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

/**
 * The shortest and longest a display name may be, in characters.
 *
 * Three at the bottom so a name is a name rather than an initial, and thirty at
 * the top because it has to fit on a gallery card beside a beer's name without
 * either of them winning.
 */
export const DISPLAY_NAME_MIN_LENGTH = 3;
export const DISPLAY_NAME_MAX_LENGTH = 30;

/**
 * What a display name may contain.
 *
 * Letters and digits, plus space, underscore, dot and hyphen inside it; the
 * first character must be alphanumeric so a name cannot open with punctuation
 * and read as a system label. ASCII-only for launch and that is
 * [settled] (PLAN-GALLERY G1): the name is an identity string rendered beside
 * other people's, and homograph confusion is an abuse surface the first release
 * does not need. Widening this is a one-line relaxation and no migration.
 */
const DISPLAY_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _.-]*$/;

/**
 * Names nobody may claim, matched case-insensitively.
 *
 * Impersonation, not profanity: every entry is a word that would make a byline
 * read as though the product itself, or somebody working on it, wrote the
 * recipe. The `bw-` / `bw_` prefix is reserved wholesale so a future system
 * account has a namespace to live in that nobody has taken first.
 *
 * A module constant rather than a `CHECK` constraint, because this list will
 * grow and a rule that needs a migration every time somebody thinks of a new
 * impersonation is a rule that gets worked around instead of extended.
 */
export const RESERVED_DISPLAY_NAMES: readonly string[] = [
  'admin',
  'beerwright',
  'brewmaster',
  'support',
  'staff',
  'moderator',
  'official',
];

/** The reserved namespace: anything starting `bw-` or `bw_`. */
const RESERVED_PREFIX_PATTERN = /^bw[-_]/;

/**
 * Why a name was refused. Stable codes, never prose — the UI owns the wording.
 *
 * `taken` is the only one the client cannot decide for itself; it comes back
 * from the server's `409` and is the reason {@link normaliseDisplayName} is not
 * the whole of validation.
 */
export type DisplayNameProblem = 'empty' | 'too-short' | 'too-long' | 'charset' | 'reserved';

/** A name that passed every local rule, or the first rule it failed. */
export type DisplayNameCheck =
  | { readonly ok: true; readonly name: string }
  | { readonly ok: false; readonly problem: DisplayNameProblem };

/**
 * Trim, and collapse internal runs of whitespace to one space.
 *
 * Separate from {@link checkDisplayName} because it is what the *field* does as
 * somebody types — "Hop  smith " is not a mistake worth an error message, it is
 * a paste — and because the server has to apply exactly this transform before
 * comparing anything. Newlines and tabs collapse too: they cannot be typed into
 * an `<input>` but they can be pasted into one.
 */
export function normaliseDisplayName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

/**
 * Whether a name may be claimed, ignoring whether somebody already has it.
 *
 * Returns the normalised form on success, so callers store what was checked
 * rather than what was typed. Uniqueness is not decided here and cannot be: it
 * is a `UNIQUE` constraint in D1 and the server maps its violation to a `409`.
 */
export function checkDisplayName(raw: string): DisplayNameCheck {
  const name = normaliseDisplayName(raw);
  if (name.length === 0) return { ok: false, problem: 'empty' };
  if (name.length < DISPLAY_NAME_MIN_LENGTH) return { ok: false, problem: 'too-short' };
  if (name.length > DISPLAY_NAME_MAX_LENGTH) return { ok: false, problem: 'too-long' };
  if (!DISPLAY_NAME_PATTERN.test(name)) return { ok: false, problem: 'charset' };
  if (isReservedDisplayName(name)) return { ok: false, problem: 'reserved' };
  return { ok: true, name };
}

/** Whether the reserved list or the reserved prefix claims this name. */
export function isReservedDisplayName(name: string): boolean {
  const lower = name.toLowerCase();
  return RESERVED_DISPLAY_NAMES.includes(lower) || RESERVED_PREFIX_PATTERN.test(lower);
}

/**
 * The uniqueness key for a name: its lowercase form.
 *
 * One function so the column, the lookup and the client's optimistic compare all
 * mean the same thing. `toLowerCase()` rather than `toLocaleLowerCase()` on
 * purpose — the input is ASCII by {@link DISPLAY_NAME_PATTERN}, and a locale
 * that maps `I` differently must not make uniqueness depend on where the Worker
 * happened to run.
 */
export function displayNameKey(name: string): string {
  return name.toLowerCase();
}

/**
 * The initials shown when there is no avatar.
 *
 * One or two characters, uppercase, from the first two words. There is no
 * fallback *image* anywhere in this feature — `/api/avatars/:userId` 404s for an
 * account with no avatar and every renderer draws these instead, which is what
 * the app already does for the signed-in user in the sidebar.
 */
export function displayNameInitials(name: string): string {
  const words = normaliseDisplayName(name).split(' ').filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const second = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return `${first}${second}`.toUpperCase();
}

// ---------------------------------------------------------------------------
// Avatars
// ---------------------------------------------------------------------------

/**
 * The square an avatar is resized to before upload, in pixels.
 *
 * 256 is twice the largest place one is drawn (a 128px profile preview) so it
 * still looks right on a 2× display, and small enough that a WebP of a
 * photograph lands well inside {@link AVATAR_MAX_BYTES}.
 */
export const AVATAR_SIZE_PX = 256;

/**
 * The hard cap on an uploaded avatar, in bytes.
 *
 * 128 KB. The client resizes to {@link AVATAR_SIZE_PX} and re-encodes, so a
 * conforming upload is a few kilobytes and this bound is never felt; it exists
 * for the client that is not this app's. **The Worker enforces it regardless of
 * what the client claims** — the body is measured after it is read, not taken
 * from `Content-Length`.
 */
export const AVATAR_MAX_BYTES = 128 * 1024;

/**
 * What may be uploaded, and it is a short list on purpose.
 *
 * WebP because that is what the client's canvas encodes to, and PNG because a
 * browser too old for `toBlob('image/webp')` falls back to it. No JPEG, no GIF,
 * no SVG — and SVG is the one worth stating: it is a document with script in it,
 * served from the app's own origin, which is a stored-XSS shape rather than an
 * image. The Worker checks **magic bytes**, not the declared content type; see
 * `sniffAvatarType`.
 */
export type AvatarContentType = 'image/webp' | 'image/png';

/** How long a browser or CDN may hold an avatar, in seconds. */
export const AVATAR_CACHE_SECONDS = 300;

/**
 * The content type of these bytes, from their leading bytes, or `null`.
 *
 * The *only* thing that decides what an avatar is. A `Content-Type` header is a
 * claim by the uploader and this route trusts none of it: a file that says
 * `image/png` and starts with `<svg` is refused here, before it can be stored
 * and later served from the app's origin.
 *
 * - PNG: the eight-byte signature `89 50 4E 47 0D 0A 1A 0A`.
 * - WebP: `RIFF` at 0, `WEBP` at 8. The four bytes between are the file length,
 *   which is not checked — a lying length makes a corrupt image, not an unsafe
 *   one, and `<img>` simply fails to draw it.
 */
export function sniffAvatarType(bytes: Uint8Array): AvatarContentType | null {
  if (bytes.length >= 8 && PNG_SIGNATURE.every((byte, index) => bytes[index] === byte)) {
    return 'image/png';
  }
  if (bytes.length >= 12 && matchesAscii(bytes, 0, 'RIFF') && matchesAscii(bytes, 8, 'WEBP')) {
    return 'image/webp';
  }
  return null;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;

function matchesAscii(bytes: Uint8Array, offset: number, ascii: string): boolean {
  for (let index = 0; index < ascii.length; index += 1) {
    if (bytes[offset + index] !== ascii.charCodeAt(index)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

/**
 * One profile, as every route that carries one renders it.
 *
 * `avatarUrl` rather than the stored R2 key, because the key is a server detail
 * and a URL is what an `<img>` needs. It carries a `v=` query the route itself
 * ignores: the object key is stable (`avatars/<userId>`, one per user), so
 * without a version a re-upload would keep showing the old image for
 * {@link AVATAR_CACHE_SECONDS}. Freshness costs a query parameter; the
 * alternative — a per-upload key — costs an orphan to collect.
 */
export interface Profile {
  readonly displayName: string;
  /** `null` when there is no avatar. Renderers draw {@link displayNameInitials}. */
  readonly avatarUrl: string | null;
  /** A short line about the brewer, or `null`. At most {@link BIO_MAX_LENGTH}. */
  readonly bio: string | null;
  /** A bare Instagram handle, or `null`. Never a URL — see {@link checkInstagram}. */
  readonly instagram: string | null;
  /** Last change, **ms since epoch** (the column is seconds). */
  readonly updatedAt: number;
}

/** `GET /api/profile` and `PUT /api/profile`. `profile` is null until one is claimed. */
export interface ProfileResponse {
  readonly ok: true;
  readonly profile: Profile | null;
}

/** A refusal from any route here. `error` is a stable code, never prose. */
export interface ProfileErrorResponse {
  readonly ok: false;
  readonly error: string;
}

/** Where the avatar bytes for an account are served from. Public. */
export function avatarPath(userId: string): string {
  return `${AVATAR_PUBLIC_BASE_PATH}/${encodeURIComponent(userId)}`;
}

/** The avatar URL as it goes on the wire, versioned so a re-upload is visible. */
export function avatarUrlFor(userId: string, updatedAtSeconds: number): string {
  return `${avatarPath(userId)}?v=${updatedAtSeconds}`;
}

// ---------------------------------------------------------------------------
// Bio and Instagram
// ---------------------------------------------------------------------------

/**
 * The longest a bio may be, in characters.
 *
 * It is a caption under a name on a shared recipe page, not an essay: 160 has
 * to sit beneath the byline on a phone without pushing the beer below the fold.
 * Raising it later costs nothing; lowering it would truncate bios people have
 * already written, which is why it starts where it can stay.
 */
export const BIO_MAX_LENGTH = 160;

/**
 * Anything in the C0/C1 control ranges, which no bio has a use for.
 *
 * biome-ignore lint/suspicious/noControlCharactersInRegex: matching control
 * characters is the entire purpose — this is the check that keeps them out of a
 * string rendered on a public page.
 */
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/;

/** The outcome of checking a bio. `bio: null` means "cleared", not "rejected". */
export type BioCheck =
  | { readonly ok: true; readonly bio: string | null }
  | { readonly ok: false; readonly problem: 'too-long' | 'charset' };

/**
 * Whether a bio may be stored, and in what form.
 *
 * Normalised exactly as a display name is — trimmed, every whitespace run
 * collapsed to one space — because the byline renders it as a single wrapped
 * line and a pasted paragraph would otherwise carry its source's line breaks
 * into a place that has no use for them.
 *
 * An empty result is `ok` with `null` rather than a `problem`: clearing a bio
 * is a thing a brewer does on purpose, and the field must not argue about it.
 */
export function checkBio(raw: string): BioCheck {
  const bio = normaliseDisplayName(raw);
  if (bio.length === 0) return { ok: true, bio: null };
  if (CONTROL_CHARACTER_PATTERN.test(bio)) return { ok: false, problem: 'charset' };
  if (bio.length > BIO_MAX_LENGTH) return { ok: false, problem: 'too-long' };
  return { ok: true, bio };
}

/** The longest an Instagram handle may be. Instagram's own limit. */
export const INSTAGRAM_MAX_LENGTH = 30;

/**
 * What an Instagram handle may contain: letters, digits, full stop, underscore.
 * Instagram's own charset, and deliberately no slash, colon or space.
 */
const INSTAGRAM_PATTERN = /^[A-Za-z0-9._]+$/;

/** Leading noise a brewer arrives with: an `@`, or the URL they were looking at. */
const INSTAGRAM_PREFIX_PATTERN = /^(?:@|(?:https?:\/\/)?(?:www\.)?instagram\.com\/)+/i;

/** The outcome of checking a handle. `handle: null` means "cleared". */
export type InstagramCheck =
  | { readonly ok: true; readonly handle: string | null }
  | { readonly ok: false; readonly problem: 'too-long' | 'charset' };

/**
 * Whether an Instagram handle may be stored, reduced to the bare handle.
 *
 * **A handle is stored and a URL never is.** That is the whole security posture
 * of this field: {@link instagramUrl} is the only thing that builds a link, it
 * builds exactly one shape, and so a public page can never be made to render an
 * href somebody else chose. A stored URL would put `javascript:`, an open
 * redirect and a link to anywhere at all inside a page we serve.
 *
 * The prefix strip is a convenience on top of that, not part of it: somebody
 * copying from the Instagram app gets `@name`, somebody copying from the
 * address bar gets the URL, and both mean the same handle.
 */
export function checkInstagram(raw: string): InstagramCheck {
  const handle = raw.trim().replace(INSTAGRAM_PREFIX_PATTERN, '').replace(/\/+$/, '');
  if (handle.length === 0) return { ok: true, handle: null };
  if (handle.length > INSTAGRAM_MAX_LENGTH) return { ok: false, problem: 'too-long' };
  if (!INSTAGRAM_PATTERN.test(handle)) return { ok: false, problem: 'charset' };
  return { ok: true, handle };
}

/** The only Instagram URL this app ever builds. */
export function instagramUrl(handle: string): string {
  return `https://www.instagram.com/${handle}`;
}

/** The upload/clear route. `PUT` sends bytes, `DELETE` removes the object. */
export function profileAvatarPath(): string {
  return `${PROFILE_BASE_PATH}/avatar`;
}

/**
 * Read a profile off a JSON payload without trusting any of it.
 *
 * Used on `/api/me` exactly as `parseEntitlement` is, and for the same reason: a
 * browser holding a cached bundle from before G1 must read a Worker that sends
 * the field, and a browser from after it must read a Worker that does not. Both
 * answer `null`, which is the same shape as "no profile claimed" and is
 * therefore already handled everywhere.
 */
export function parseProfile(value: unknown): Profile | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as {
    displayName?: unknown;
    avatarUrl?: unknown;
    updatedAt?: unknown;
    bio?: unknown;
    instagram?: unknown;
  };
  if (typeof record.displayName !== 'string' || record.displayName.length === 0) return null;
  return {
    displayName: record.displayName,
    avatarUrl: typeof record.avatarUrl === 'string' ? record.avatarUrl : null,
    // Degraded rather than dropped, exactly as the avatar is: a bad optional
    // field is a field the reader does without, not a profile that vanishes.
    // Re-checked rather than taken on trust, because the handle becomes an href.
    bio: readChecked(record.bio, (raw) => {
      const checked = checkBio(raw);
      return checked.ok ? checked.bio : null;
    }),
    instagram: readChecked(record.instagram, (raw) => {
      const checked = checkInstagram(raw);
      return checked.ok ? checked.handle : null;
    }),
    updatedAt:
      typeof record.updatedAt === 'number' && Number.isFinite(record.updatedAt)
        ? record.updatedAt
        : 0,
  };
}

/** Apply a checker to a value that may not be a string at all. */
function readChecked(value: unknown, check: (raw: string) => string | null): string | null {
  return typeof value === 'string' ? check(value) : null;
}
